/**
 * Tracks whether the browser believes it has network connectivity AND
 * whether the MAMA backend is reachable.
 *
 * Combines:
 *   - navigator.onLine (instant signal from the OS)
 *   - a periodic ping against GET /api/health (truth source)
 *   - axios interceptor errors classified as "Network Error"
 *
 * Returns { online, backendReachable, lastPing }.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";

const PING_INTERVAL_MS = 25_000;
const PING_TIMEOUT_MS = 5_000;

export function useNetworkStatus() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [backendReachable, setBackendReachable] = useState(true);
  const [lastPing, setLastPing] = useState(null);
  const mounted = useRef(true);

  const ping = useCallback(async () => {
    try {
      await api.get("/health", { timeout: PING_TIMEOUT_MS });
      if (!mounted.current) return;
      setBackendReachable(true);
      setLastPing(new Date());
    } catch {
      if (!mounted.current) return;
      setBackendReachable(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const onUp = () => { setOnline(true); ping(); };
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);

    ping();
    const tm = setInterval(ping, PING_INTERVAL_MS);
    return () => {
      mounted.current = false;
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
      clearInterval(tm);
    };
  }, [ping]);

  return { online, backendReachable, lastPing, ping };
}
